import React, { useContext, useState } from "react";
import { Button } from "primereact/button";

import * as Yup from "yup";
import Flex from "../../../../components/shared-styles/flexi";
import Input from "../../../../components/form/input"; 
import { formatErrors, handleInput, handleSubmit } from "../../../../services/utils/processors";
import { ToastContext } from "../../../../services/contexts/toast-context";
import styled from "styled-components";

// -- Styled Components for Redesign --
const CardContainer = styled.div`
  min-width: 50vw;
  background-color: var(--surface-card, #ffffff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    min-width: 100%;
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--surface-border, #f3f4f6);
  padding-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--text-color, #374151);
    font-weight: 600;
  }
`;

const GridLine = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;


const Certifications = ({ defaultData = [], loading = false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const [certifications, setCertifications] = useState(Array.isArray(defaultData) ? defaultData : []);
  const [certificationError, setCertificationError] = useState({});

  const schema = Yup.array().of(
    Yup.object({
      title: Yup.string().required("Certification Title is required"),
      issuer: Yup.string().nullable().notRequired(),
      credential_id: Yup.string().nullable().notRequired(),
      credential_url: Yup.string().url("Invalid URL").nullable().notRequired(),
    })
  );

  const handleInputChange = (value, index, field) => {
    const newCertifications = [...certifications];
    newCertifications[index][field] = value;
    setCertifications(newCertifications);
  };

  const addCertification = () => {
    setCertifications([
      ...certifications,
      { title: "", issuer: "", credential_id: null, credential_url: null },
    ]);
  };

  const removeCertification = (index) => {
    const newCertifications = certifications.filter((_, i) => i !== index);
    setCertifications(newCertifications);
  };

  const handleSubmitData = () => {
    handleSubmit(certifications, schema, setCertificationError, onSave);
    if (certificationError && Object.entries(certificationError).length !== 0) {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: formatErrors(certificationError),
      });
    }
    setCertificationError({});
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <Button 
          label="Add Certification" 
          icon="pi pi-plus" 
          outlined
          className="p-button-secondary" 
          onClick={addCertification} 
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
        {certifications.map((certification, index) => (
          <CardContainer key={index}>
            <CardHeader>
              <h3>{certification.title ? certification.title : `Certification ${index + 1}`}</h3>
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => removeCertification(index)}
                aria-label="Remove Certification"
                style={{ padding: "8px" }}
              />
            </CardHeader>
            <Flex direction="column" align="stretch" gap="20px" style={{width: "100%"}}>
              
              <GridLine>
                <Input
                  value={certification.title}
                  label="Title"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "title")}
                  error={certificationError?.[index]?.title}
                />
                <Input
                  value={certification.issuer}
                  label="Issuer"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "issuer")}
                  error={certificationError?.[index]?.issuer}
                />
              </GridLine>

              <GridLine>
                <Input
                  value={certification.credential_id}
                  label="Credential ID"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "credential_id")}
                  error={certificationError?.[index]?.credential_id}
                />
                <Input
                  value={certification.credential_url}
                  label="Credential URL"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "credential_url")}
                  error={certificationError?.[index]?.credential_url}
                />
              </GridLine>
              
            </Flex>
          </CardContainer>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
        <Button 
          label="Save & Continue" 
          icon="pi pi-check" 
          loading={loading} 
          disabled={loading} 
          className="p-button-primary" 
          onClick={handleSubmitData} 
        />
      </div>
    </div>
  );
};

export default Certifications;
